import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Crown, Medal, Award, Shield, RefreshCw, TrendingUp, Star,
  Users, Target, BookOpen, Heart, ChevronDown, ChevronUp
} from 'lucide-react';

const TIER_ICONS = { crown: Crown, medal: Medal, award: Award, shield: Shield };

const BREAKDOWN_LABELS = {
  curriculum: { label: 'Curriculum', icon: BookOpen, color: 'text-teal-600' },
  engagement: { label: 'Engagement', icon: Heart, color: 'text-blue-600' },
  compliance: { label: 'Compliance', icon: Shield, color: 'text-emerald-600' },
  goals: { label: 'Goals', icon: Target, color: 'text-amber-600' },
};

function TierBadge({ tier, size = 'md' }) {
  const Icon = TIER_ICONS[tier.icon] || Shield;
  const sizeClasses = size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2 py-0.5 text-xs';
  return (
    <Badge
      className={`${sizeClasses} border-0 font-semibold gap-1`}
      style={{ backgroundColor: `${tier.color}20`, color: tier.color }}
      data-testid={`tier-badge-${tier.name.toLowerCase()}`}
    >
      <Icon size={size === 'lg' ? 16 : 12} /> {tier.name}
    </Badge>
  );
}

function ScoreRing({ score, size = 80 }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 90 ? '#a78bfa' : score >= 70 ? '#f59e0b' : score >= 50 ? '#94a3b8' : '#d97706';

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#f1f5f9" strokeWidth="6" />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color}
        strokeWidth="6" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central" className="rotate-90"
        style={{ transform: 'rotate(90deg)', transformOrigin: 'center', fill: color, fontSize: size * 0.3, fontWeight: 700 }}
      >
        {score}
      </text>
    </svg>
  );
}

export default function ScoreboardPage() {
  const { axiosAuth, user } = useAuth();
  const [leaderboard, setLeaderboard] = useState(null);
  const [myScore, setMyScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const isResident = ['client', 'housing_resident'].includes(user?.role) || user?.is_housing_resident;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosAuth();
      if (isResident) {
        // Residents only see their own score
        const myRes = await api.get('/scoring/my-score');
        setMyScore(myRes.data);
        setLeaderboard(null);
      } else {
        // Staff see the full leaderboard
        const lbRes = await api.get('/scoring/leaderboard');
        setLeaderboard(lbRes.data);
      }
    } catch {
      toast.error('Failed to load scoreboard');
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, isResident]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const dist = leaderboard?.tier_distribution || {};

  return (
    <div className="space-y-6" data-testid="scoreboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Scoreboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Resident progress rankings and tier standings</p>
        </div>
        <Button variant="outline" onClick={loadData} className="rounded-full gap-2" data-testid="refresh-scoreboard-btn">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* My Score (for residents) */}
      {myScore && (
        <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800" data-testid="my-score-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <ScoreRing score={myScore.composite_score} size={100} />
              <div className="flex-1">
                <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Your Score</p>
                <p className="text-white text-xl font-bold">{myScore.user_name}</p>
                <div className="mt-2">
                  <TierBadge tier={myScore.tier} size="lg" />
                </div>
                <div className="grid grid-cols-4 gap-3 mt-4">
                  {Object.entries(myScore.breakdown).map(([key, val]) => {
                    const meta = BREAKDOWN_LABELS[key];
                    return (
                      <div key={key} className="text-center">
                        <meta.icon size={14} className={`mx-auto mb-1 ${meta.color}`} />
                        <p className="text-white font-bold text-sm">{val}</p>
                        <p className="text-white/40 text-[10px]">{meta.label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tier Distribution */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { name: 'Platinum', count: dist.Platinum || 0, color: '#a78bfa', icon: Crown },
          { name: 'Gold', count: dist.Gold || 0, color: '#f59e0b', icon: Medal },
          { name: 'Silver', count: dist.Silver || 0, color: '#94a3b8', icon: Award },
          { name: 'Bronze', count: dist.Bronze || 0, color: '#d97706', icon: Shield },
        ].map(t => (
          <Card key={t.name} className="border-0 shadow-sm rounded-xl" data-testid={`tier-count-${t.name.toLowerCase()}`}>
            <CardContent className="p-4 text-center">
              <t.icon size={24} style={{ color: t.color }} className="mx-auto mb-1" />
              <p className="text-xl font-bold">{t.count}</p>
              <p className="text-xs text-muted-foreground">{t.name}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Leaderboard */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp size={16} className="text-teal-600" /> Rankings ({leaderboard?.total || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(!leaderboard?.leaderboard || leaderboard.leaderboard.length === 0) ? (
            <div className="text-center py-8">
              <Star size={40} className="mx-auto text-slate-200 mb-3" />
              <p className="text-sm text-muted-foreground">No scored residents yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {leaderboard.leaderboard.map((entry) => {
                const expanded = expandedId === entry.user_id;
                return (
                  <div key={entry.user_id} data-testid={`leaderboard-entry-${entry.rank}`}>
                    <div
                      className={`flex items-center gap-3 p-3 rounded-xl transition-colors cursor-pointer ${
                        entry.rank <= 3 ? 'bg-gradient-to-r from-slate-50 to-teal-50' : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                      onClick={() => setExpandedId(expanded ? null : entry.user_id)}
                    >
                      {/* Rank */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                        entry.rank === 1 ? 'bg-amber-100 text-amber-700' :
                        entry.rank === 2 ? 'bg-slate-200 text-slate-700' :
                        entry.rank === 3 ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {entry.rank}
                      </div>

                      {/* User */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{entry.user_name}</p>
                        <p className="text-[10px] text-muted-foreground">{entry.program_name}</p>
                      </div>

                      {/* Score & Tier */}
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-lg tabular-nums" data-testid={`score-${entry.rank}`}>
                          {entry.composite_score}
                        </span>
                        <TierBadge tier={entry.tier} />
                      </div>

                      {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                    </div>

                    {/* Expanded breakdown */}
                    {expanded && (
                      <div className="mt-1 ml-11 mr-4 p-3 rounded-xl bg-white border border-slate-100 space-y-2">
                        {Object.entries(entry.breakdown).map(([key, val]) => {
                          const meta = BREAKDOWN_LABELS[key];
                          const weight = entry.weights?.[key] ? `${(entry.weights[key] * 100).toFixed(0)}%` : '';
                          return (
                            <div key={key} className="flex items-center gap-2">
                              <meta.icon size={14} className={meta.color} />
                              <span className="text-xs w-20">{meta.label}</span>
                              <div className="flex-1">
                                <Progress value={val} className="h-1.5" />
                              </div>
                              <span className="text-xs font-medium w-8 text-right">{val}</span>
                              <span className="text-[10px] text-muted-foreground w-8">{weight}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
